import Modal from "@/UI/Modal";
import ViewBtn from "@/components/Buttons/ViewBtn";
import CommonDelete from "@/components/CommonUi/CommonDelete";
import TableComponent from "@/components/CommonUi/TableComponent";
import UserPrivateComponent from "@/components/PrivacyComponent/UserPrivateComponent";
import { apiSlice } from "@/redux/rtk/features/apiSlice/apiSlice";
import { useGetDealsConvertedQuery } from "@/redux/rtk/features/CRM/deal/dealApi";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { AiOutlineDelete } from "react-icons/ai";
import { FaWhatsapp } from "react-icons/fa";
import { Link, useNavigate } from "react-router-dom";
import { Tag, Drawer, Button, Tabs } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import useDealFilter from "./useDealFilter";
import { AdvancedFilter } from "@/components/CommonUi/AdvancedFilter";
import AddDeal from "./AddDeal";
import usePermissions from "@/utils/usePermissions";
import BulkDelete from "@/components/CommonUi/BulkDelete";
import BulkUpdateDealDetails from "./BulkUpdateDealDetails";

export default function GetAllDeals() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { permissions } = usePermissions();
  const canReadCommission = permissions?.includes("read-commission");
  const canCreateDeal = permissions?.includes("create-deal");
  const [pageConfig, setPageConfig] = useState({
    page: 1,
    count: 10,
    isAgentAdded: "false", // Default to Direct deals
  });
  const { data, isLoading } = useGetDealsConvertedQuery(pageConfig);
  const [addDealVisible, setAddDealVisible] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [bulkUpdateVisible, setBulkUpdateVisible] = useState(false);
  const [activeTab, setActiveTab] = useState("direct");

  const handleTabChange = (key) => {
    setActiveTab(key);
    setPageConfig((prev) => ({
      ...prev,
      page: 1,
      isAgentAdded: key === "agent" ? "true" : "false",
    }));
  };

  const items = [
    {
      key: "direct",
      label: t("deal.directDeals") || "Direct Deals",
    },
    {
      key: "agent",
      label: t("deal.agentDeals") || "Agent Deals",
    },
  ];


  const onSelectChange = (newSelectedRowKeys) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
  };





  const columns = [
    {
      title: t("common.id"),
      key: "id",
      dataIndex: "id",
    },
    {
      title: t("deal.dealName") || "Deal Name",
      key: "dealName",
      render: (item) => (
        <div className="flex items-center gap-2">
          <Link to={`/admin/deal/${item?.id}`}>{item?.dealName}</Link>
          {item?.duplicateIds?.length > 0 && (
            <Tag color="red" className="text-[10px] px-1.5 py-0.5 font-bold rounded-full">
              {t("deal.duplicate")}
            </Tag>
          )}
        </div>
      ),
      renderCsv: (item) => item?.dealName,
    },
    {
      title: t("deal.duplicates"),
      key: "duplicates",
      render: (item) => {
        const dups = item?.duplicateIds || [];
        if (!dups.length) return "-";
        return (
          <div className="flex flex-wrap gap-1.5 items-center" onClick={(e) => e.stopPropagation()}>
            <Tag color="error" className="font-bold flex items-center gap-1 m-0">
              ⚠️ {t("deal.duplicateWith")}
            </Tag>
            {dups.map((dupId) => (
              <Link
                key={dupId}
                to={`/admin/deal/${dupId}`}
                className="px-2 py-0.5 bg-red-100 hover:bg-red-200 text-red-700 font-bold rounded text-xs transition-colors border border-red-300 shadow-sm"
                onClick={(e) => e.stopPropagation()}
              >
                #{dupId}
              </Link>
            ))}
          </div>
        );
      },
      renderCsv: (item) => (item?.duplicateIds?.length ? item.duplicateIds.join(", ") : "-"),
    },
    {
      title: t("deal.dealOwner") || "Deal Owner",
      dataIndex: "ownerFullName",
      key: "ownerFullName",
      render: (name) => name || '-',
      renderCsv: (name) => name || '-',
    },
    {
      title: t("deal.stage") || "Stage",
      key: "stageName",
      render: (item) => item.stageName || '-',
      renderCsv: (item) => item.stageName || '-',
    },

    {
      title: t("deal.probability") || "Probability",
      key: "probability",
      dataIndex: "probability",
      render: (probability) => `${probability || 0}%`,
      renderCsv: (probability) => `${probability || 0}%`,
    },
    {
      title: t("deal.scholarship") || "Scholarship",
      key: "scholarship",
      dataIndex: "scholarship",
      render: (scholarship) => `${scholarship || 0}%`,
      renderCsv: (scholarship) => `${scholarship || 0}%`,
    },
    {
      title: t("common.description"),
      key: "description",
      dataIndex: "description",
      render: (description) => (
        <div className="max-w-[200px] truncate" title={description}>
          {description || "-"}
        </div>
      ),
      renderCsv: (description) => description || "-",
    },
    {
      title: t("common.phone"),
      key: "phone",
      render: (item) => {
        const phone = item?.phone || item?.contactPhone || item?.leadPhone;
        return (
          <div className="flex items-center gap-2">
            <span>{phone || "-"}</span>
            {phone && (
              <a
                href={`https://wa.me/${phone.replace(/[^0-9]/g, "")}`}
                target="_blank"
                rel="noreferrer"
                className="text-green-500 hover:text-green-600 text-lg"
                onClick={(e) => e.stopPropagation()}
              >
                <FaWhatsapp />
              </a>
            )}
          </div>
        );
      },
      renderCsv: (item) => item?.phone || item?.contactPhone || item?.leadPhone || "-",
    },
    {
      title: t("lead.mobile"),
      key: "mobile",
      render: (item) => {
        const mobile = item?.mobile || item?.contactMobile || item?.leadMobile;
        return (
          <div className="flex items-center gap-2">
            <span>{mobile || "-"}</span>
            {mobile && (
              <a
                href={`https://wa.me/${mobile.replace(/[^0-9]/g, "")}`}
                target="_blank"
                rel="noreferrer"
                className="text-green-500 hover:text-green-600 text-lg"
                onClick={(e) => e.stopPropagation()}
              >
                <FaWhatsapp />
              </a>
            )}
          </div>
        );
      },
      renderCsv: (item) => item?.mobile || item?.contactMobile || item?.leadMobile || "-",
    },
    {
      title: t("lead.degree") || "Degree",
      key: "degree",
      dataIndex: "degree",
      render: (degree) => degree || "-",
      renderCsv: (degree) => degree || "-",
    },
    {
      title: t("lead.university") || "Universities",
      key: "universitiesNames",
      dataIndex: "universitiesNames",
      render: (unis) => unis ? (
        <div className="max-w-[200px] truncate" title={unis}>{unis}</div>
      ) : "-",
      renderCsv: (unis) => unis || "-",
    },
    {
      title: t("lead.major") || "Major",
      key: "majorName",
      dataIndex: "majorName",
      render: (major) => major || "-",
      renderCsv: (major) => major || "-",
    },
    {
      title: t("deal.agent"),
      key: "agentName",
      dataIndex: "agentName",
      render: (agentName) => agentName || "-",
      renderCsv: (agentName) => agentName || "-",
    },
    {
      title: t("deal.priority"),
      key: "priority",
      dataIndex: "priority",
      render: (priority) => (
        <Tag color={priority === "Transfer" ? "blue" : "cyan"}>
          {priority ? priority.toUpperCase() : "DIRECT"}
        </Tag>
      ),
    },
    {
      title: t("deal.uploadStatus"),
      key: "uploadStatus",
      dataIndex: "uploadStatus",
      render: (status) => (
        <Tag color={status === "done" ? "green" : "orange"}>
          {status === "done" ? t("deal.done").toUpperCase() : t("deal.pending").toUpperCase()}
        </Tag>
      ),
    },
    {
      title: t("deal.agentCommission"),
      key: "agentCommissionPaid",
      dataIndex: "agentCommissionPaid",
      render: (status) => (
        <Link to="/admin/deal/bulk-commission">
          <Tag color={status === "yes" ? "green" : "red"} className="cursor-pointer">
            {status === "yes" ? t("common.yes").toUpperCase() : t("common.no").toUpperCase()}
          </Tag>
        </Link>
      ),
    },
    {
      title: t("common.createdAt"),
      key: "createdAt",
      dataIndex: "createdAt",
      render: (date) => new Date(date).toLocaleString(),
      renderCsv: (date) => new Date(date).toLocaleString(),
    },
    {
      title: t("common.updatedAt"),
      key: "updatedAt",
      dataIndex: "updatedAt",
      render: (date) => new Date(date).toLocaleString(),
      renderCsv: (date) => new Date(date).toLocaleString(),
    },
    {
      title: "",
      key: "action",
      render: (item) => [
        {
          label: <ViewBtn title={t("common.view")} path={`/admin/deal/${item?.id}`} />,
          key: "view",
        },
        {
          label: (
            <CommonDelete
              id={item?.id}
              title={t("common.delete")}
              permission={"delete-deal"}
              deleteThunk={apiSlice.endpoints.deleteDeal.initiate}
              query={pageConfig}
              className="bg-white text-black"
            />
          ),
          key: "delete",
        },
      ],
      csvOff: true,
    },
  ].filter(col => col.key !== "agentCommissionPaid" || canReadCommission);

  /* eslint-disable-next-line */
  const [filtersOpen, setFiltersOpen] = useState(false);
  const filters = useDealFilter(filtersOpen);

  return (
    <UserPrivateComponent permission={["readAll-deal", "readSingle-deal"]}>
      <div className="bg-white px-4 pt-4 rounded-t-lg">
        <Tabs activeKey={activeTab} onChange={handleTabChange} items={items} />
      </div>
      <TableComponent
        list={data?.getAllDeal}
        columns={columns}
        loading={isLoading}
        total={data?.totalDeal}
        setPageConfig={setPageConfig}
        pageConfig={pageConfig}
        title={t("deal.dealList")}
        isSearch
        filters={filters}
        exportPermission="export-deal"
        printPermission="print-deal"
        rowSelection={rowSelection}
        componentTitle={t("deal.title")}
        extraFilter={
          <AdvancedFilter
            page="deal"
            filters={filters}
            pageConfig={pageConfig}
            setPageConfig={setPageConfig}
            onOpen={() => setFiltersOpen(true)}
          />
        }
        extra={
          <div className="flex gap-2">
            {selectedRowKeys.length > 0 && (
              <>
                <Button
                  type="primary"
                  onClick={() => setBulkUpdateVisible(true)}
                >
                  {t("common.update")} ({selectedRowKeys.length})
                </Button>
                <BulkDelete
                  selectedRowKeys={selectedRowKeys}
                  deleteThunk={apiSlice.endpoints.deleteDeal.initiate}
                  query={pageConfig}
                  clearSelection={() => setSelectedRowKeys([])}
                />
              </>
            )}
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setAddDealVisible(true)}
              disabled={!canCreateDeal}
              style={!canCreateDeal ? { display: "none" } : {}}
            >
              {t("common.add") || "Add"}
            </Button>
          </div>
        }
        onRow={(item) => {
          const isDuplicate = item?.duplicateIds?.length > 0;
          return {
            onClick: (event) => {
              // Prevent navigation when user is selecting text
              const selection = window.getSelection?.().toString();
              if (selection && selection.length > 0) {
                return;
              }
              navigate(`/admin/deal/${item?.id}`);
            },
            className: `cursor-pointer ${isDuplicate ? "bg-red-50/80 hover:bg-red-100" : ""}`,
            style: isDuplicate ? { backgroundColor: "#fff1f0" } : {},
          };
        }}
      />
      <Drawer
        title={t("deal.addDeal")}
        width={window.innerWidth > 768 ? 600 : "100%"}
        onClose={() => setAddDealVisible(false)}
        open={addDealVisible}
        destroyOnClose
      >
        <AddDeal onClose={() => setAddDealVisible(false)} />
      </Drawer>

      <Modal
        title={t("deal.bulkUpdateDetails")}
        open={bulkUpdateVisible}
        onCancel={() => setBulkUpdateVisible(false)}
        footer={null}
        destroyOnClose
      >
        <BulkUpdateDealDetails
          selectedRowKeys={selectedRowKeys}
          onClose={() => setBulkUpdateVisible(false)}
          clearSelection={() => setSelectedRowKeys([])}
        />
      </Modal>
    </UserPrivateComponent>
  );
}

